import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, BarChart3, FolderLock } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { KpiStatCard } from "@/components/KpiStatCard";
import { TrendChart } from "@/components/TrendChart";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { InvestorRightsPanel } from "@/components/InvestorRightsPanel";
import { AsksPanel } from "@/components/investor/AsksPanel";
import { QAPanel } from "@/components/investor/QAPanel";
import { DataRoomsPanel } from "@/components/investor/DataRoomsPanel";
import { MyImpactCard } from "@/components/investor/MyImpactCard";
import api from "@/lib/api";
import { METRIC_DEFS, computeDelta } from "@/lib/metrics";
import { SweatEquityCard } from "@/components/SweatEquityCard";
import { AnchorTabs } from "@/components/investor/AnchorTabs";
import { formatPeriodLabel, formatDate } from "@/lib/format";

const SECTIONS = [
  { id: "rights", label: "Rights" }, { id: "metrics", label: "Metrics" }, { id: "asks", label: "Asks" }, { id: "updates", label: "Updates" },
  { id: "documents", label: "Documents" }, { id: "datarooms", label: "Data rooms" }, { id: "qa", label: "Q&A" },
];

export default function CompanyHealth() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const res = await api.get(`/portal/companies/${id}`); setData(res.data); }
      catch (e) { console.error("Failed to load company", e); navigate("/portal"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const company = data?.company;
  const metrics = data?.metrics || [];
  const updates = data?.updates || [];
  const latest = metrics[metrics.length - 1];
  const prev = metrics[metrics.length - 2];
  const seriesFor = (key) => metrics.map((m) => m[key]);
  const customKpis = [];
  const seenCustom = new Set();
  metrics.forEach((m) => (m.custom || []).forEach((c) => {
    if (c.key && !seenCustom.has(c.key)) { seenCustom.add(c.key); customKpis.push(c); }
  }));

  const back = <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/portal")} data-testid="back-to-portfolio"><ArrowLeft className="h-4 w-4" /> Portfolio</Button>;

  return (
    <AppShell title={company?.name || "Company"} subtitle={[company?.sector, company?.stage].filter(Boolean).join(" · ")} actions={back}>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <AnchorTabs sections={SECTIONS} />
          {company?.description && <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{company.description}</p>}

          <MyImpactCard companyId={id} />

          {data?.sweat_equity?.summary?.total_accrued > 0 && (
            <div className="mb-8"><SweatEquityCard summary={data.sweat_equity.summary} entries={data.sweat_equity.entries} history={data.sweat_equity.history} variant="investor" /></div>
          )}

          <section id="rights" className="mb-8 scroll-mt-32"><InvestorRightsPanel companyId={id} /></section>

          <section id="metrics" className="scroll-mt-32">
          {metrics.length === 0 ? (
            <EmptyState icon={BarChart3} title="No metrics shared yet" description="This company hasn't published metrics yet. Check back soon." />
          ) : (
            <>
              <h2 className="mb-2 text-sm font-medium text-muted-foreground">Health snapshot · {formatPeriodLabel(latest?.period)}</h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {METRIC_DEFS.map((def) => (
                  <KpiStatCard key={def.key} def={def} value={latest?.[def.key]} delta={prev ? computeDelta(latest?.[def.key], prev?.[def.key]) : null} series={seriesFor(def.key)} />
                ))}
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card className="rounded-xl border bg-card p-5"><div className="mb-3 text-sm font-semibold text-foreground">MRR trend</div><TrendChart data={metrics} dataKey="mrr" type="currency" /></Card>
                <Card className="rounded-xl border bg-card p-5"><div className="mb-3 text-sm font-semibold text-foreground">Runway trend</div><TrendChart data={metrics} dataKey="runway_months" type="months" color="hsl(var(--chart-2))" /></Card>
                {customKpis.slice(0, 4).map((d) => (
                  <Card key={d.key} className="min-h-[220px] rounded-xl border bg-card p-5" data-testid={`custom-chart-${d.key}`}>
                    <div className="mb-3 text-sm font-semibold text-foreground">{d.name} trend</div>
                    <TrendChart
                      data={metrics.map((m) => ({ ...m, [`c_${d.key}`]: (m.custom || []).find((c) => c.key === d.key)?.value }))}
                      dataKey={`c_${d.key}`}
                      type={d.unit === "currency" ? "currency" : d.unit === "percent" ? "percent" : "number"}
                      color="hsl(var(--chart-4))"
                    />
                  </Card>
                ))}
              </div>
            </>
          )}
          </section>

          <section id="asks" className="mt-8 scroll-mt-32"><AsksPanel companyId={id} /></section>

          <section id="updates" className="mt-8 scroll-mt-32">
            <h2 className="mb-3 h-display text-lg font-semibold text-foreground">Updates</h2>
            {updates.length === 0 ? (
              <EmptyState icon={FileText} title="No updates yet" description="Published updates from this company will appear here." />
            ) : (
              <div className="space-y-2">
                {updates.map((u) => (
                  <Card key={u.id} onClick={() => navigate(`/portal/updates/${u.id}`)} className="flex cursor-pointer items-center justify-between rounded-xl border bg-card p-4 transition-shadow hover:shadow-[var(--shadow-md)]" data-testid={`portal-update-${u.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold text-foreground">{u.title}</div>
                        {u.status === "imported" && <Badge variant="outline" className="shrink-0 border-amber-200 bg-amber-50 text-amber-700 font-normal" data-testid={`portal-imported-badge-${u.id}`}>Archive</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{formatPeriodLabel(u.period)} · {u.status === "imported" ? `Originally sent ${formatDate(u.published_at)}` : `Published ${formatDate(u.published_at)}`}</div>
                    </div>
                    <Badge variant="secondary" className="font-normal">Read</Badge>
                  </Card>
                ))}
              </div>
            )}
          </section>

          <section id="documents" className="mt-8 scroll-mt-32">
            <h2 className="mb-3 flex items-center gap-2 h-display text-lg font-semibold text-foreground"><FolderLock className="h-5 w-5 text-primary" /> Documents</h2>
            <DocumentsPanel companyId={id} />
          </section>

          <section id="datarooms" className="mt-8 scroll-mt-32"><DataRoomsPanel companyId={id} /></section>

          <section id="qa" className="mt-8 scroll-mt-32"><QAPanel companyId={id} /></section>
        </>
      )}
    </AppShell>
  );
}
